import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Divider,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { getGridButtonSelectorStyles } from "../theme";
import {
  POPPER_CONFIG,
  PAPER_CONFIG,
  SPACING,
} from "../constants/filterSelectors";

export const VIDEO_STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "unpublished", label: "Unpublished" },
];

export const VIDEO_VISIBILITY_OPTIONS = [
  { value: "public", label: "Public" },
  { value: "internal", label: "Internal Only" },
];

const labelFor = (options, value) =>
  options.find((option) => option.value === value)?.label || value;

const getDisplayText = (selectedStatuses, selectedVisibilities) => {
  const parts = [];

  if (selectedStatuses.length > 0) {
    parts.push(
      selectedStatuses
        .map((status) => labelFor(VIDEO_STATUS_OPTIONS, status))
        .join(", "),
    );
  }
  if (selectedVisibilities.length > 0) {
    parts.push(
      selectedVisibilities
        .map((visibility) => labelFor(VIDEO_VISIBILITY_OPTIONS, visibility))
        .join(", "),
    );
  }

  return parts.length > 0 ? parts.join(" • ") : "All Videos";
};

const VideosFilterSelector = ({
  selectedStatuses = [],
  selectedVisibilities = [],
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const triggerBoxShadow =
    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)";
  const textFieldRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [localStatuses, setLocalStatuses] = useState(selectedStatuses);
  const [localVisibilities, setLocalVisibilities] = useState(
    selectedVisibilities,
  );

  useEffect(() => {
    setLocalStatuses(selectedStatuses);
    setLocalVisibilities(selectedVisibilities);
  }, [selectedStatuses, selectedVisibilities]);

  const open = Boolean(anchorEl);

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : textFieldRef.current);
  };

  const handleClose = () => {
    setLocalStatuses(selectedStatuses);
    setLocalVisibilities(selectedVisibilities);
    setAnchorEl(null);
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        statuses: localStatuses,
        visibilities: localVisibilities,
      });
    }
    setAnchorEl(null);
  };

  const toggle = (setter) => (value) => {
    setter((previous) =>
      previous.includes(value)
        ? previous.filter((entry) => entry !== value)
        : [...previous, value],
    );
  };

  return (
    <>
      {isMobile ? (
        <Tooltip title="Video Filters">
          <IconButton
            ref={textFieldRef}
            onClick={handleClick}
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              boxShadow: triggerBoxShadow,
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHover,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <FilterListRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          ref={textFieldRef}
          fullWidth
          size="small"
          label="Video Filters"
          value={getDisplayText(selectedStatuses, selectedVisibilities)}
          onClick={handleClick}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
            height: "100%",
            "& .MuiInputBase-root": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              boxShadow: triggerBoxShadow,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
            },
            "& .MuiInputBase-input": {
              py: "8px !important",
              color: theme.palette.text.primary,
              fontSize: "0.875rem",
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                edge="end"
                onClick={handleClick}
                sx={{ padding: 0.5 }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            ),
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: POPPER_CONFIG.zIndex }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={PAPER_CONFIG.elevation}
            sx={{
              p: PAPER_CONFIG.padding,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.background.default}`,
              borderRadius: PAPER_CONFIG.borderRadius,
              maxHeight: "80vh",
              overflowY: "auto",
              width: isMobile ? "min(90vw, 360px)" : 420,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: SPACING.containerGap,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: theme.palette.primary.text2,
                  }}
                >
                  Status
                </Typography>
                <Grid container spacing={SPACING.gridSpacing}>
                  {VIDEO_STATUS_OPTIONS.map((option) => {
                    const isSelected = localStatuses.includes(option.value);
                    return (
                      <Grid size={12} key={option.value}>
                        <Box
                          sx={{
                            ...getGridButtonSelectorStyles(theme, isSelected),
                            py: SPACING.buttonPadding,
                            minHeight: "auto",
                          }}
                          onClick={() => toggle(setLocalStatuses)(option.value)}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: isSelected
                                ? "#ffffff"
                                : theme.palette.primary.main,
                              fontWeight: isSelected ? "bold" : "normal",
                            }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {isMobile ? (
                <Divider sx={{ my: SPACING.containerGap }} />
              ) : (
                <Divider orientation="vertical" flexItem />
              )}

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: theme.palette.primary.text2,
                  }}
                >
                  Who can watch
                </Typography>
                <Grid container spacing={SPACING.gridSpacing}>
                  {VIDEO_VISIBILITY_OPTIONS.map((option) => {
                    const isSelected = localVisibilities.includes(option.value);
                    return (
                      <Grid size={12} key={option.value}>
                        <Box
                          sx={{
                            ...getGridButtonSelectorStyles(theme, isSelected),
                            py: SPACING.buttonPadding,
                            minHeight: "auto",
                          }}
                          onClick={() =>
                            toggle(setLocalVisibilities)(option.value)
                          }
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: isSelected
                                ? "#ffffff"
                                : theme.palette.primary.main,
                              fontWeight: isSelected ? "bold" : "normal",
                            }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: SPACING.gridSpacing,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ borderRadius: PAPER_CONFIG.borderRadius }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: PAPER_CONFIG.borderRadius,
                  "&:hover": {
                    bgcolor: theme.palette.primary.buttonHoverSelected,
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default VideosFilterSelector;
